import { Observable, Subject } from 'rxjs';
import { LanguageLabel } from './i18n.constants';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Brand, BrandService } from '../brand';
import { switchMap } from 'rxjs/operators';
import { LanguageConfig } from '../config/language.config';

@Injectable()
export class I18nService {
  constructor(
    private translateService: TranslateService,
    private brandService: BrandService
  ) {
    this.brandService.brand.subscribe((brand) => {
      this.updateTranslationsForBrand(brand);
    });
    this.setLanguage(this.language);
  }

  /**
   * List of supported languages
   */
  supportedLanguages = [
    'bg', // Bulgarian
    'cz', // Czech
    'da', // Danish
    'de', // German
    'gr', // Greek
    'en', // English
    'us', // English (US)
    'es', // Spanish
    'se', // Spanish (US)
    'fi', // Finish
    'fr', // French
    'it', // Italian
    'jp', // Japanese
    'nl', // Dutch
    'pl', // Polish
    'ro', // Romanian
    'ru', // Russian
    'sv', // Swedish
    'tr', // Turkish
    'cn', // Chinese
    'pt' // Portuguese
  ];

  /**
   * Default language
   */
  defaultLanguage = 'en';

  /**
   * Map of language to i18n files. File paths must start with the assets/ directory
   * and end with .json extension, eg. assets/skrill/i18n/en-GB.json.
   */
  i18nFiles = new Map<string, string>().set('en', 'assets/skrill-and-neteller/i18n/sn-en-GB.json');

  /**
   * Subject for change of language events
   */
  languageChange = new Subject<string>();

  /**
   * Boolean flag to tell whether translations file is loaded
   */
  translationsLoaded: boolean;

  /**
   * 2-letter language code, i.e. en
   */
  language = 'en';

  private updateTranslationsForBrand(brand: Brand): void {
    this.supportedLanguages = LanguageConfig[brand].supportedLanguages;
    this.i18nFiles = LanguageConfig[brand].i18nFiles;
    this.setLanguage(this.language);
  }

  /**
   * Returns list of supported languages.
   * @returns {string[]} List of 2-letter language codes
   */
  getSupportedLanguages(): string[] {
    return this.supportedLanguages;
  }

  /**
   * Gets default language.
   * @returns {string} 2-letter language code, i.e. en
   */
  getDefaultLanguage(): string {
    return this.defaultLanguage;
  }

  /**
   * Returns filename for requested language. If not passed or not supported,
   * then filename for default language is returned.
   * @param {string} language 2-letter language code, i.e. en
   * @returns {string} filename
   */
  getI18nFilename(language?: string): string {
    let filePath = this.i18nFiles.get(this.defaultLanguage);
    if (language && this.getSupportedLanguages().includes(language) && this.i18nFiles.has(language)) {
      filePath = this.i18nFiles.get(language);
    }

    return filePath && filePath.match(/i18n\/(.*)\.json/)[1];
  }

  /**
   * Gets current language.
   * @returns {string} 2-letter language code, i.e. en
   */
  getLanguage(): string {
    return this.language;
  }

  /**
   * Sets language.
   * @param {string} language 2-letter language code, i.e. en
   */
  setLanguage(language: string): void {
    const lcLangCode = language.toLowerCase();
    if (this.getSupportedLanguages().includes(lcLangCode)) {
      this.language = lcLangCode;
    } else {
      this.language = this.defaultLanguage;
    }
    this.languageChange.next(language);
    const setLanguageString = this.getI18nFilename(this.getLanguage());
    this.translateService
      .use(setLanguageString)
      .pipe(switchMap(() => this.translateService.use(setLanguageString)))
      .subscribe(() => this.setI18nFileLoaded(true));
  }

  /**
   * Gets i18n file loaded status
   * @returns {boolean} true or false
   */
  isI18nFileLoaded(): boolean {
    return this.translationsLoaded;
  }

  /**
   * Sets i18n file loaded status
   * @param {boolean} isLoaded true or false
   */
  setI18nFileLoaded(isLoaded: boolean): void {
    this.translationsLoaded = isLoaded;
  }

  /**
   * Returns the name of the language, eg. "Deutsch" for "de"
   * @param {string} language 2-letter language code, i.e. en
   */
  getLanguageName(language: string): string {
    return LanguageLabel[language.toLowerCase()];
  }

  /**
   * Returns language change observable to subscribe to.
   */
  getLanguageChangeSubject(): Observable<string> {
    return this.languageChange.asObservable();
  }
}
